/**
 * Copy final (no provisorio) de los correos disparados desde el cliente vía el bus de
 * notifications/ (dispatchNotification.js los renderiza sobre el layout de marca — ver
 * project-log/initiatives/diseno-sistema-email.md, Fase 2 "Copy final ES/EN de cada tipo").
 *
 * Centralizado acá en vez de en cada línea de auth-service.js/study-service.js/
 * admin-service.js: son 3 archivos distintos escribiendo al mismo type de notificación,
 * y antes de esto cada uno tenía su propio texto "provisorio" suelto en la llamada.
 * No incluye los tipos admin_* (siempre en español, arman su copy en
 * functions/src/notifications/*.js — ver notifyAdmins.js) ni mentor_feedback (el cuerpo
 * es el feedback real que escribió el mentor, no hay copy que centralizar).
 */

// ─── welcome ───────────────────────────────────────────────────────────────
// El único correo con foco explícito en explicar qué es el ministerio y qué gana la
// persona con la cuenta, no solo agradecer el registro (a pedido explícito de Víctor,
// 2026-08-14). Voz tomada de translation.json → ministry.leaders.intro y ministry.areas,
// no inventada: 20+ años de trabajo misionero de Phil y Carolyn English, discipulado
// arraigado en la Palabra, en México.
export function welcomeCopy(lang) {
  if (lang === 'en') {
    return {
      title: 'Welcome to Voices of Hope',
      body: [
        "Thank you for creating your account. Voices of Hope is a Christian ministry that walks alongside people, churches, and pastors in Mexico, rooted in the Word of God. It's led by Phil and Carolyn English, with more than twenty years of missionary work.",
        "Your account isn't just a place to read: it's where your journey is kept. Here's what you'll find:",
      ],
      listItems: [
        '• A devotional every morning.',
        '• Bible studies in Formación, with your progress saved.',
        '• Someone to walk with you, if you choose to ask. No one walks alone.',
        '• Videos and stories from people already on this path.',
      ],
    };
  }
  return {
    title: 'Bienvenido a Voces de Esperanza',
    body: [
      'Gracias por crear tu cuenta. Voces de Esperanza es un ministerio cristiano que camina junto a personas, iglesias y pastores en México, arraigado en la Palabra de Dios. Lo lideran Phil y Carolyn English, con más de veinte años de trabajo misionero.',
      'Tu cuenta no es solo un lugar para leer: es donde tu camino queda guardado. Esto es lo que encontrarás:',
    ],
    listItems: [
      '• Un devocional cada mañana.',
      '• Estudios bíblicos en Formación, con tu avance guardado.',
      '• Alguien que te acompañe, si decides pedirlo. Nadie camina solo.',
      '• Videos e historias de gente que ya va por este camino.',
    ],
  };
}

// ─── mentor_feedback (solo título — el cuerpo es la respuesta real del mentor) ─────────
export function mentorFeedbackTitle(lang) {
  return lang === 'en' ? 'Your mentor replied to you' : 'Tu acompañante te respondió';
}

// ─── session_reminder ───────────────────────────────────────────────────────
export function sessionReminderCopy(lang, { date, time }) {
  if (lang === 'en') {
    return {
      title: 'Your mentoring session',
      body: `You have a session scheduled for ${date} at ${time}. A calendar invite is attached.`,
    };
  }
  return {
    title: 'Tu sesión de acompañamiento',
    body: `Tienes una sesión agendada para el ${date} a las ${time}. Va adjunta la invitación de calendario.`,
  };
}

// ─── weekly_checkin (mentor, es-only por decisión ya tomada — ver dispatchNotification.js) ──
export function weeklyCheckinCopy({ color }) {
  return {
    title: 'Semáforo de Gracia de la semana',
    body: `Tu acompañado reportó su semana: ${color}. Entra a tu panel para leer la nota completa.`,
  };
}

// ─── mentor_assigned (mentor, es-only) ─────────────────────────────────────
// ─── note_shared (mentor) ───────────────────────────────────────────────────
// Único de los tipos "mentor" que sí respeta el idioma del discípulo que comparte (los
// otros dos, weekly_checkin/mentor_assigned, están fijados a 'es' por decisión ya tomada
// — ver dispatchNotification.js). Antes mandaba título/cuerpo en español aunque `language`
// llegara 'en', por más que el remitente y el CTA sí cambiaban de idioma — corregido acá.
export function noteSharedCopy(lang, { displayName }) {
  if (lang === 'en') {
    return {
      title: `${displayName || 'Someone'} shared a note with you`,
      body: 'They wrote a reflection and chose to show it to you.',
    };
  }
  return {
    title: `${displayName || 'Alguien'} compartió una nota contigo`,
    body: 'Escribió una reflexión y decidió mostrártela.',
  };
}

export function mentorAssignedCopy({ studentDisplayName }) {
  return {
    title: `Ahora acompañas a ${studentDisplayName || 'un nuevo acompañado'}`,
    body: 'Te asignaron un nuevo acompañado. Su avance ya aparece en tu panel.',
  };
}

// ─── lesson_completed (mentor) ──────────────────────────────────────────────
export function lessonCompletedCopy(lang, { studentName, lessonTitle }) {
  if (lang === 'en') {
    return {
      title: `${studentName || 'Someone'} finished a lesson`,
      body: `They finished "${lessonTitle}". A short message from you goes further than you'd think.`,
    };
  }
  return {
    title: `${studentName || 'Alguien'} terminó una lección`,
    body: `Terminó "${lessonTitle}". Un mensaje corto tuyo pesa más de lo que parece.`,
  };
}

// ─── program_milestone (mentor) ─────────────────────────────────────────────
export function programMilestoneCopy(lang, { studentName, programTitle }) {
  if (lang === 'en') {
    return {
      title: `${studentName || 'Someone'} completed a program`,
      body: `They finished "${programTitle}". Worth celebrating with them.`,
    };
  }
  return {
    title: `${studentName || 'Alguien'} completó un programa`,
    body: `Terminó "${programTitle}". Vale la pena celebrarlo con él o ella.`,
  };
}

// ─── note_feedback (solo título — el cuerpo es la respuesta real del mentor) ────
export function noteFeedbackTitle(lang) {
  return lang === 'en' ? 'Someone replied to your note' : 'Respondieron tu nota';
}

// ─── lesson_ack (solo título — el cuerpo es el mensaje real del mentor) ────────
export function lessonAckTitle(lang) {
  return lang === 'en' ? 'Your mentor saw your lesson' : 'Tu acompañante vio tu lección';
}
